import { useLayoutEffect, useRef, useSyncExternalStore } from 'react'
import type { TipStore } from './item-actions'

/** Kept this far from the window's edges. */
const EDGE = 8

/**
 * The portfolio calendar's one tooltip (CAP-1418): whatever item is under the
 * pointer or has keyboard focus, in a few short lines. A single element for
 * the whole grid, not one per item; it never takes the pointer, and a scroll
 * drops it (the item under it moves away without a pointer leave).
 *
 * Purely visual: every item's accessible name already says the same, so a
 * screen reader is not pointed at it.
 */
export function PortfolioTooltip({ store }: { store: TipStore }) {
  const state = useSyncExternalStore(store.subscribe, store.get, store.get)
  const ref = useRef<HTMLDivElement>(null)

  // Kept on screen: pulled in from the right edge, and above the item when
  // there is no room below. Measured before the browser paints it.
  useLayoutEffect(() => {
    const element = ref.current
    if (!state || !element) return
    const { offsetWidth: width, offsetHeight: height } = element
    const left = Math.max(EDGE, Math.min(state.anchor.x, window.innerWidth - width - EDGE))
    const top =
      state.anchor.y + height > window.innerHeight - EDGE
        ? Math.max(EDGE, state.anchor.above - height)
        : state.anchor.y
    element.style.left = `${left}px`
    element.style.top = `${top}px`
  }, [state])

  if (!state) return null
  const { tip, anchor } = state
  return (
    <div
      ref={ref}
      aria-hidden="true"
      data-testid="portfolio-tooltip"
      className="pointer-events-none fixed z-40 flex w-max max-w-[260px] flex-col gap-0.5 rounded-[10px] border border-borderStrong bg-surface2 px-2.5 py-2 text-xs leading-4 text-text shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
      style={{ left: anchor.x, top: anchor.y }}
    >
      <span className="font-semibold">{tip.title}</span>
      {tip.lines.map((line, index) => (
        <span key={index} className="text-textMuted">
          {line}
        </span>
      ))}
      {tip.warn && <span className="text-amber">{tip.warn}</span>}
    </div>
  )
}
